import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Crown } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FREE = ["1 muse", "3 Content Cards total", "All 6 formats", "Identity Lock on every prompt"];
const PRO = ["3 muses", "Unlimited Content Cards", "Weekly Planner", "Section-by-section regenerate", "Priority new formats"];

const Feature = ({ children }) => (
  <li className="flex gap-3 text-sm"><Check size={16} className="text-[#9C4DDC] mt-0.5 shrink-0" />{children}</li>
);

export default function PricingCards({ compact }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [interval, setInterval] = useState("yearly");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const isPro = user && user.plan === "pro";

  const checkout = async () => {
    if (!user) return nav("/signup");
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { interval, origin_url: window.location.origin, promo_code: code });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatErr(err)); setBusy(false); }
  };

  return (
    <div className={`grid md:grid-cols-2 gap-5 ${compact ? "" : "max-w-4xl"}`} data-testid="pricing-cards">
      <div className="panel p-7 flex flex-col" data-testid="pricing-free-card">
        <p className="eyebrow">Free</p>
        <p className="font-display text-5xl mt-4">$0</p>
        <p className="muted text-sm mt-1">Try the studio with one muse.</p>
        <ul className="space-y-3 mt-8 mb-8">{FREE.map((f) => <Feature key={f}>{f}</Feature>)}</ul>
        <button className="btn-secondary w-full mt-auto" onClick={() => nav(user ? "/create" : "/signup")} data-testid="pricing-free-button">
          {user ? (isPro ? "Included in Pro" : "Current plan") : "Start free"}
        </button>
      </div>
      <div className="panel p-7 flex flex-col relative overflow-hidden border-[#9C4DDC]" data-testid="pricing-pro-card">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#9C4DDC]/15 blur-3xl pointer-events-none" />
        <p className="eyebrow flex items-center gap-2"><Crown size={12} /> Pro</p>
        <div className="flex gap-1 p-1 mt-4 rounded-[16px] border border-[#F0EAF7] w-fit bg-[#FAF7FE]">
          {["monthly", "yearly"].map((i) => (
            <button key={i} onClick={() => setInterval(i)} data-testid={`pricing-interval-${i}`}
              className={`px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.12em] transition-colors ${interval === i ? "bg-[#9C4DDC] text-white" : "muted"}`}>
              {i === "monthly" ? "Monthly" : "Annual"}
            </button>
          ))}
        </div>
        <p className="font-display text-5xl mt-5" data-testid="pricing-pro-price">
          {interval === "yearly" ? "$149" : "$19"}<span className="text-base muted font-sans">/{interval === "yearly" ? "year" : "month"}</span>
        </p>
        {interval === "yearly" ? (
          <p className="text-sm text-[#9C4DDC] mt-1" data-testid="pricing-best-value">Best value — save $79 a year</p>
        ) : <p className="text-sm muted mt-1">Or $149/yr — about 4 months free</p>}
        <ul className="space-y-3 mt-8 mb-6">{PRO.map((f) => <Feature key={f}>{f}</Feature>)}</ul>
        {!isPro && (
          <input className="field mb-3 uppercase" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Promo code (optional)" data-testid="pricing-promo-input" />
        )}
        <button className="btn-primary w-full mt-auto" onClick={isPro ? () => nav("/account") : checkout} disabled={busy} data-testid="pricing-pro-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {isPro ? "You're Pro — manage billing" : code === "CERTIFIED30" ? "Start 30-day free trial" : "Go Pro"}
        </button>
      </div>
    </div>
  );
}
